import { toast } from "sonner";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const normaliseMessage = (value) => {
  if (typeof value === "string") return value.trim();
  if (value == null) return "";
  return String(value).trim();
};

const addErrorMessage = (messages, item, fieldName = "") => {
  if (Array.isArray(item)) {
    item.forEach((entry) => addErrorMessage(messages, entry, fieldName));
    return;
  }

  if (item && typeof item === "object") {
    const message = normaliseMessage(item.message ?? item.errorMessage ?? item.Message ?? item.ErrorMessage);
    if (message) {
      messages.push(fieldName ? `${fieldName}: ${message}` : message);
      return;
    }

    Object.entries(item).forEach(([key, value]) => addErrorMessage(messages, value, key));
    return;
  }

  const message = normaliseMessage(item);
  if (message) messages.push(fieldName ? `${fieldName}: ${message}` : message);
};

const getApiErrorMessages = (error, fallbackMessage = DEFAULT_ERROR_MESSAGE) => {
  const responseData = error?.response?.data ?? error?.data ?? error;
  const errors = responseData?.errors ?? responseData?.validationResult?.errors ?? responseData?.ValidationResult?.Errors;
  const messages = [];

  if (errors) {
    if (Array.isArray(errors)) {
      errors.forEach((item) => addErrorMessage(messages, item));
    } else if (typeof errors === "object") {
      Object.entries(errors).forEach(([fieldName, value]) => {
        addErrorMessage(messages, value, fieldName);
      });
    } else {
      addErrorMessage(messages, errors);
    }
  }

  if (responseData?.validationResult?.errors && responseData.validationResult.errors !== errors) {
    addErrorMessage(messages, responseData.validationResult.errors);
  }

  if (messages.length === 0 && responseData?.validationResult?.Errors && responseData.validationResult.Errors !== errors) {
    addErrorMessage(messages, responseData.validationResult.Errors);
  }

  if (messages.length === 0 && Array.isArray(responseData?.Errors)) {
    responseData.Errors.forEach((item) => addErrorMessage(messages, item));
  } else if (messages.length === 0 && responseData?.Errors && typeof responseData.Errors === "object") {
    Object.entries(responseData.Errors).forEach(([fieldName, value]) => addErrorMessage(messages, value, fieldName));
  }

  if (messages.length === 0) {
    [
      responseData?.message,
      responseData?.Message,
      responseData?.title,
      responseData?.Title,
      responseData?.detail,
      responseData?.Detail,
      error?.message,
    ].some((value) => {
      const message = normaliseMessage(value);
      if (!message) return false;
      messages.push(message);
      return true;
    });
  }

  if (messages.length === 0) {
    messages.push(fallbackMessage);
  }

  return [...new Set(messages)];
};

const ToastHelper = {
  success(message, options) {
    return toast.success(message, options);
  },

  info(message, options) {
    return toast.info(message, options);
  },

  warning(message, options) {
    return toast.warning(message, options);
  },

  error(messageOrError, options) {
    if (messageOrError?.response) {
      return this.apiError(messageOrError);
    }
    return toast.error(messageOrError || DEFAULT_ERROR_MESSAGE, options);
  },

  apiError(error, fallbackMessage = DEFAULT_ERROR_MESSAGE) {
    const messages = getApiErrorMessages(error, fallbackMessage);
    toast.error(messages.join("\n"), {
      className: "whitespace-pre-line",
    });
    return messages;
  },

  apiSuccess(response, fallbackMessage = "Saved successfully.") {
    const message = response?.data?.message || response?.message || fallbackMessage;
    return toast.success(message);
  },

  dismiss(toastId) {
    return toast.dismiss(toastId);
  },

  promise(promise, messages) {
    return toast.promise(promise, messages);
  },
};

export { getApiErrorMessages };
export default ToastHelper;
